import type { Identifier } from "ra-core";

import { formatKr } from "../../deals/DealLinesEditor";
import { formatDateShort } from "../../misc/osloDate";

// Rangeringen på Oversikt «Fokus» (spesifikasjonen kap. 4). Deterministisk og
// forklarbar: hver rad får poengene til sitt sterkeste signal, pluss 3 per
// ekstra signal og pluss forfalte dager (maks 20). Vektene står her og bare
// her: spørringen og «Slik rangeres listen» leser den samme tabellen.

export type SignalKind =
  | "notification"
  | "next_step_overdue"
  | "deal_no_step"
  | "task_overdue"
  | "lead_unowned"
  | "lead_no_contact"
  | "lead_parse_failed"
  | "deal_stalled"
  | "offer_expiring"
  | "customer_never"
  | "task_today"
  | "agreement_maintenance"
  | "agreement_renewal"
  | "customer_quiet"
  | "drift";

export type SignalDef = {
  weight: number;
  /** Teksten i «Slik rangeres listen». */
  label: string;
  /** Rød prikk (forfalt eller mangler neste steg); ellers gul. */
  tone: "red" | "yellow";
};

// Rekkefølgen her er rekkefølgen i popoveren.
export const SIGNALS: Record<SignalKind, SignalDef> = {
  notification: { weight: 45, label: "Nytt varsel til deg", tone: "red" },
  next_step_overdue: {
    weight: 40,
    label: "Neste steg på en mulighet er forfalt",
    tone: "red",
  },
  deal_no_step: {
    weight: 35,
    label: "Åpen mulighet uten neste steg",
    tone: "red",
  },
  task_overdue: { weight: 30, label: "Forfalt oppgave", tone: "red" },
  lead_unowned: { weight: 30, label: "Lead uten eier", tone: "red" },
  lead_no_contact: {
    weight: 30,
    label: "Lead uten logget kontakt",
    tone: "red",
  },
  lead_parse_failed: {
    weight: 30,
    label: "Skjema som ikke kunne tolkes",
    tone: "red",
  },
  deal_stalled: {
    weight: 25,
    label: "Mulighet stille lenger enn stadiets grense",
    tone: "yellow",
  },
  offer_expiring: {
    weight: 25,
    label: "Tilbud som utløper innen 3 dager",
    tone: "yellow",
  },
  customer_never: {
    weight: 22,
    label: "Kunde aldri kontaktet",
    tone: "yellow",
  },
  task_today: {
    weight: 20,
    label: "Oppgave som forfaller i dag",
    tone: "yellow",
  },
  agreement_maintenance: {
    weight: 18,
    label: "Avtale ikke vedlikeholdt på 6 måneder",
    tone: "yellow",
  },
  agreement_renewal: {
    weight: 18,
    label: "Avtale som fornyes innen 60 dager",
    tone: "yellow",
  },
  customer_quiet: {
    weight: 15,
    label: "Kunde uten kontakt på 90 dager",
    tone: "yellow",
  },
  drift: {
    weight: 10,
    label: "Funn på nettstedet: 10 + 2 per funn, maks 20",
    tone: "yellow",
  },
};

export const DRIFT_MAX = 20;
export const EXTRA_SIGNAL_POINTS = 3;
export const MAX_OVERDUE_POINTS = 20;
/** Det som har ligget lenger enn dette, flyttes til «Rydd opp». */
export const CLEANUP_DAYS = 30;
/** Antall rader i «Gjør nå» før «Vis alle». */
export const VISIBLE_ROWS = 6;
/** Kunder uten kontakt så lenge, får signalet «Ingen kontakt». */
export const QUIET_DAYS = 90;

export type Signal = {
  kind: SignalKind;
  /** Dager forfalt, stille eller uten kontakt. */
  days?: number;
  /** Antall funn (drift), måneder (vedlikehold). */
  count?: number;
  /** Stadiets navn (muligheter). */
  stage?: string | null;
  /** Dato i teksten (fornyelse, tilbud). */
  date?: string | null;
  /** Fri tekst i varselet. */
  text?: string | null;
};

export const signalWeight = (signal: Signal) =>
  signal.kind === "drift"
    ? Math.min(SIGNALS.drift.weight + 2 * (signal.count ?? 0), DRIFT_MAX)
    : SIGNALS[signal.kind].weight;

const OVERDUE_KINDS: SignalKind[] = ["next_step_overdue", "task_overdue"];

// Sterkeste signal først. Ved lik vekt står det forfalte foran.
export const sortSignals = (signals: Signal[]) =>
  [...signals].sort(
    (a, b) =>
      signalWeight(b) - signalWeight(a) ||
      Number(OVERDUE_KINDS.includes(b.kind)) -
        Number(OVERDUE_KINDS.includes(a.kind)),
  );

export const scoreSignals = (signals: Signal[]) => {
  if (!signals.length) return 0;
  const sorted = sortSignals(signals);
  const overdue = Math.max(
    0,
    ...signals
      .filter((s) => OVERDUE_KINDS.includes(s.kind))
      .map((s) => s.days ?? 0),
  );
  return (
    signalWeight(sorted[0]) +
    EXTRA_SIGNAL_POINTS * (sorted.length - 1) +
    Math.min(overdue, MAX_OVERDUE_POINTS)
  );
};

export type FocusRowType =
  | "task"
  | "next_step"
  | "opportunity"
  | "lead"
  | "customer"
  | "agreement"
  | "drift"
  | "notification";

export const ROW_TYPE_LABEL: Record<FocusRowType, string> = {
  task: "Oppgave",
  next_step: "Neste steg",
  opportunity: "Mulighet",
  lead: "Lead",
  customer: "Kunde",
  agreement: "Avtale",
  drift: "Driftsradar",
  notification: "Varsel",
};

export type FocusRow = {
  /** «task:12», «deal:5», «company:3» … Også nøkkelen for nåler. */
  key: string;
  type: FocusRowType;
  title: string;
  signals: Signal[];
  /** Fylles av rankRows. */
  score: number;
  /** Eier eller ansvarlig: styrer Mine, Team og Uten eier. */
  ownerId: Identifier | null;
  /** Leads og kunder uten eier står øverst i Team og Uten eier. */
  unowned?: boolean;
  /** Lukket for Mine-filteret: varsler er alltid dine. */
  alwaysMine?: boolean;
  companyId?: Identifier | null;
  companyName?: string | null;
  /** Datoen som utløste raden (frist, stadium, siste kontakt). Eldst først. */
  triggerDate: string | null;
  /** Verdi på mulighet eller avtale (tie-break og merke). */
  value?: number | null;
  /** «2 400 kr/mnd» når muligheten bare har månedlig beløp. */
  monthlyOnly?: boolean;
  /** Dato til høyre (frist eller fornyelse). */
  markerDate?: string | null;
  /** Grått etter grunnen: «Oppgave · Bruun Eiendom». */
  meta: string[];
  path: string;
  /** Ligger over grensen og hører hjemme i «Rydd opp». */
  cleanup: boolean;
  /** Festet i dag: rekkefølgen nålene ble satt i. */
  pinOrder?: number;
  /** Kilden raden kom fra, til handlingene. */
  source?: unknown;
};

// Grunnteksten for ett signal (kap. 5.1).
export const reasonFor = (signal: Signal): string => {
  const n = signal.days ?? 0;
  switch (signal.kind) {
    case "notification":
      return signal.text || "Nytt varsel";
    case "task_overdue":
      return n <= 1 ? "Forfalt i går" : `Forfalt for ${n} dager siden`;
    case "task_today":
      return "Forfaller i dag";
    case "next_step_overdue":
      return n <= 1
        ? "Neste steg forfalt i går"
        : `Neste steg forfalt for ${n} dager siden`;
    case "deal_no_step":
      return signal.stage
        ? `${signal.stage}, ingen neste steg`
        : "Ingen neste steg";
    case "deal_stalled":
      return signal.stage
        ? `Stille i ${n} dager i ${signal.stage.toLowerCase()}`
        : `Stille i ${n} dager`;
    case "offer_expiring":
      return `Tilbudet utløper ${formatDateShort(signal.date)}`;
    case "lead_unowned":
      return n === 1 ? "Lead uten eier i 1 dag" : `Lead uten eier i ${n} dager`;
    case "lead_no_contact":
      return "Ingen kontakt logget";
    case "lead_parse_failed":
      return "Kunne ikke tolke skjemaet";
    case "customer_never":
      return "Aldri kontaktet";
    case "customer_quiet":
      return `Ingen kontakt på ${n} dager`;
    case "agreement_maintenance":
      return signal.count == null
        ? "Aldri vedlikeholdt"
        : `Ikke vedlikeholdt på ${signal.count} måneder`;
    case "agreement_renewal":
      return `Fornyes ${formatDateShort(signal.date)}`;
    case "drift":
      return signal.count === 1
        ? "1 funn på nettstedet"
        : `${signal.count ?? 0} funn på nettstedet`;
  }
};

/** Grunnen som vises: det sterkeste signalet. */
export const rowReason = (row: Pick<FocusRow, "signals">) =>
  row.signals.length ? reasonFor(sortSignals(row.signals)[0]) : "";

/** Alle signaler med vekt, til verktøytipset på grunnen. */
export const signalBreakdown = (row: Pick<FocusRow, "signals">) =>
  sortSignals(row.signals).map((s) => `${reasonFor(s)} (${signalWeight(s)})`);

export const rowTone = (row: Pick<FocusRow, "signals">) =>
  row.signals.length
    ? SIGNALS[sortSignals(row.signals)[0].kind].tone
    : "yellow";

// Rekkefølgen (kap. 4.3): festet i dag først (i den rekkefølgen de ble
// festet), så høyest poeng, eldste utløsende dato, høyest verdi og til slutt
// alfabetisk.
export const compareRows = (a: FocusRow, b: FocusRow) => {
  const pa = a.pinOrder ?? Number.POSITIVE_INFINITY;
  const pb = b.pinOrder ?? Number.POSITIVE_INFINITY;
  if (pa !== pb) return pa < pb ? -1 : 1;
  return (
    b.score - a.score ||
    (a.triggerDate ?? "9999").localeCompare(b.triggerDate ?? "9999") ||
    (b.value ?? 0) - (a.value ?? 0) ||
    a.title.localeCompare(b.title, "nb")
  );
};

export type Scope = "mine" | "team" | "unowned";

export const inScope = (
  row: FocusRow,
  scope: Scope,
  me: Identifier | null | undefined,
) => {
  if (scope === "team") return true;
  if (scope === "unowned") return !!row.unowned;
  if (row.alwaysMine) return true;
  return (
    me != null && row.ownerId != null && String(row.ownerId) === String(me)
  );
};

// Poeng, filter og rekkefølge. I Team og Uten eier står leads og kunder uten
// eier øverst (fiks 4), men under det som er festet.
export const rankRows = (
  rows: FocusRow[],
  {
    scope,
    me,
    pins = [],
  }: { scope: Scope; me: Identifier | null | undefined; pins?: string[] },
) => {
  const scored = rows
    .filter((row) => row.signals.length > 0 && inScope(row, scope, me))
    .map((row) => {
      const pinIndex = pins.indexOf(row.key);
      return {
        ...row,
        score: scoreSignals(row.signals),
        pinOrder: pinIndex >= 0 ? pinIndex : undefined,
      };
    });
  const unownedFirst = scope !== "mine";
  scored.sort((a, b) => {
    if (unownedFirst && a.pinOrder == null && b.pinOrder == null) {
      const ua = Number(!!a.unowned);
      const ub = Number(!!b.unowned);
      if (ua !== ub) return ub - ua;
    }
    return compareRows(a, b);
  });
  // Festede rader hører hjemme i «Gjør nå», selv når de er gamle.
  return {
    now: scored.filter((row) => !row.cleanup || row.pinOrder != null),
    cleanup: scored.filter((row) => row.cleanup && row.pinOrder == null),
  };
};

/** Verdi som merke: «45 000 kr» eller «2 400 kr/mnd». */
export const formatValue = (value: number, monthlyOnly = false) =>
  `${formatKr(Math.round(value))} kr${monthlyOnly ? "/mnd" : ""}`;
